import React, { useState, useEffect } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Spinner, Button } from 'lib/ui/components'
import { Price } from 'lib/shared/components'
import { DEFAULT_CURRENCY_CODE } from 'lib/core/resources/constants'
import { useProductSelectorResults } from 'hooks/productSelector'
import { AddToCart } from './components/AddToCart'
import iconSatisfaction from './resources/images/icon-garantee.png'
import iconActive from './resources/images/icon-active.png'
import iconDrips from './resources/images/icon-drips.png'
import { HeaderContainer, ProductContainer, InfoContainer } from './styled'
import { ModalExtraWrapper } from 'scenes/Product/components/OptionsSection/styled'
import { getProductAttributeList } from 'lib/core/data/common/productAttributeList/selectors'
import { normalizeProductCustomAttributes, AddToCartModal } from 'lib/modules/catalog'
import { useCustomGTM } from 'hooks/GTM'
import { SubscriptionOptions } from 'app/sofia/scenes/ProductSelector/scenes/Results/components/SubscriptionOptions'
import { getPackQty } from 'scenes/Product/components/OptionsSection'

const PROMO_SUB_FREQUENCY = 2

export const PerfectProduct = () => {
  const history = useHistory()
  const [shipmentFrequency, setShipmentFrequency] = useState(4)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const availableOptions = useSelector(getProductAttributeList) || {}
  const { triggerQuizResult } = useCustomGTM()

  const {
    primaryProduct,
    sku,
    usesByDay,
    promotionValue,
    productUrl
  } = useProductSelectorResults() ?? {}
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [packsToAdd, setPacksToAdd] = useState(parseInt(usesByDay))

  useEffect(() => {
    if (sku && primaryProduct) {
      triggerQuizResult({
        sku,
        brand,
        bladderLeakageLevel: absorbency
      })
    }
    // eslint-disable-next-line
  }, [sku, primaryProduct])

  if (!sku && !primaryProduct) {
    history.push('/product-selector')
    return null
  }

  if (!primaryProduct) {
    return <Spinner />
  }

  const {
    id,
    price_range: price,
    name,
    image,
    visiture_custom_attributes: visitureCustomAttributes
  } = primaryProduct

  const { customAttributesIndex } = normalizeProductCustomAttributes(visitureCustomAttributes, availableOptions)

  const imageUrl = image?.url
  const [packageQty] = customAttributesIndex?.package_qty ?? []
  const [brand] = customAttributesIndex?.manufacturer ?? []
  const [absorbency] = customAttributesIndex?.primary_bladder_leakage ?? []
  const availableFrequencies = customAttributesIndex.available_frequencies

  const quantityPerDay = parseInt(usesByDay)
  const piecesQty = parseInt(packageQty)
  const packs = (getPackQty?.(piecesQty, quantityPerDay, shipmentFrequency)) ?? 0
  const promotionalPacks = getPackQty?.(piecesQty, quantityPerDay, PROMO_SUB_FREQUENCY)
  const promoSubTotalPieces = promotionalPacks * piecesQty

  return (
    <div>
      <AddToCartModal
        isModalVisible={showSuccessModal}
        setIsModalVisible={setShowSuccessModal}
        productName={name}
        productImage={imageUrl}
        sku={sku}
        continueShoppingLink='/bladder-control'
      >
        <ModalExtraWrapper>
          {shipmentFrequency > 0 && (
            <div>
              <strong>
                {promotionValue && (
                  <>Two Week Special Offer and </>
                )}
                Subscription
              </strong>
              <p>Ships every {shipmentFrequency} weeks</p>
            </div>
          )}
        </ModalExtraWrapper>
      </AddToCartModal>
      <HeaderContainer>
        <h1>We found your perfect product!</h1>
        <p>Now that we know you, here's what we recommend.</p>
      </HeaderContainer>
      <ProductContainer>
        <div className='column column--image'>
          <img src={imageUrl} alt='product name' />
        </div>
        <div className='column column--info'>
          <strong>{name}</strong>
          <div className='note'>
            <h3>$2.99 Special Offer With Subscription</h3>
            <p>
              To get you started, we’re giving you {PROMO_SUB_FREQUENCY} weeks of {name} for just <Price currency={DEFAULT_CURRENCY_CODE} value={promotionValue} />!
            </p>
            <strong>
              Your 2 week supply includes {promoSubTotalPieces} pieces
            </strong>
          </div>
        </div>
      </ProductContainer>
      <InfoContainer>
        <h3>We love this product for you because:</h3>
        <div className='info__items'>
          <div className='info__item'>
            <img src={iconSatisfaction} alt='100% satisfaction guaranteed' />
            <p>100% satisfaction guaranteed</p>
          </div>
          <div className='info__item'>
            <img src={iconActive} alt='Good for active lifestyles' />
            <p>Good for active lifestyles</p>
          </div>
          <div className='info__item'>
            <img src={iconDrips} alt='Great for drips' />
            <p>Great for drips</p>
          </div>
        </div>
        <div className='info__shipment'>
          <div>
            <SubscriptionOptions
              productId={id}
              frequencies={availableFrequencies}
              productPrice={price}
              shipmentFrequency={shipmentFrequency}
              setShipmentFrequency={setShipmentFrequency}
              packageQty={packageQty}
              setPacksToAdd={setPacksToAdd}
              quantityPerDay={quantityPerDay}
              title='How often do you want your subscription shipments?'
              subtitle={
                <p>
                  Receive 10% off every subscription order!
                  <br />
                  Easy to change, cancel or add to your subscription at any time!
                </p>
              }
            />
          </div>
        </div>
        <AddToCart
          isPromotion
          sku={sku}
          qty={packs}
          shipmentFrequency={shipmentFrequency}
          quantityPerDay={quantityPerDay}
          onSuccess={setShowSuccessModal}
        />
        <div className='info__actions'>
          <Link to={`/${productUrl}#oneTime` ?? '/'} target='_blank' rel='noopener noreferrer'>
            No thanks! I just want to buy one time at <Price currency={DEFAULT_CURRENCY_CODE} value={price.maximum_price.final_price.value} /> per bag
          </Link>
        </div>
        <Button onClick={() => history.push('/bladder-control')}>
          Shop All Bladder Control Products
        </Button>
      </InfoContainer>
    </div>
  )
}
